import moment from 'moment';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { generateClient } from '@aws-amplify/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as icons_solid from '@fortawesome/free-solid-svg-icons';
import { getSubjectContacts as _getSubjectContacts } from '@/graphql/small-queries';
import ExpandButton from '@/components/ExpandButton';
import { engagementSummary } from '@/apis/mockDbCrud';
import { dash, outcome_color } from '@/utils';
import TranscriptModal from '@/TranscriptModal';
import useSubscription from '@/hooks/useSubscription';
import { buildLocalDateRangeUtcFilter, isWithinInclusiveDateRange } from '@/utils/dashboardDateRange';
import RootContext from '@/RootContext';
import './Widgets.scss'

const recordingIcon = (<FontAwesomeIcon className="fg-purple" icon={icons_solid.faVolumeLow} size="lg" />);
const transcriptIcon = (<FontAwesomeIcon className="fg-purple" icon={icons_solid.faQuoteLeft} size="lg" />);

const client = generateClient();

const getSubjectContacts = ({ filters, limit, sorting = { field: 'started_at', order: 'desc' } } = {}) => {
  return client.graphql({ query: _getSubjectContacts, variables: { filters, limit, sorting } })
    .then(response => {
      return response.data.getSubjectContacts?.subject_contacts
    });
};

const EngagementSummaryCard = ({ endDate, selectedPeriod = 'last 30 days', startDate }) => {
  const rootContext = useContext(RootContext);
  const auth = rootContext.get('auth');
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState();

  const filters_ref = useRef();
  const queryConfig = useMemo(() => {
    const startedAtFilter = buildLocalDateRangeUtcFilter(startDate, endDate);
    return {
      filters: startedAtFilter ? [{ started_at: startedAtFilter }] : undefined,
      sorting: { field: 'started_at', order: 'desc' },
    };
  }, [endDate, startDate]);
  filters_ref.current = queryConfig;
  useSubscription({ filters_ref, rankings_key: 'subject_contacts', setRows });

  const handleExpand = () => {
    navigate('/EngagementSummaryExpanded', {
      state: {
        endDate,
        selectedPeriod,
        startDate,
      },
    });
    console.log("Expand button clicked");
  };

  useEffect(() => {
    let isActive = true;
    getSubjectContacts({ ...queryConfig, limit: 100 })
      .then(res => {
        if (isActive) setRows(res || []);
      });
    return () => {
      isActive = false;
    };
  }, [queryConfig]);

  const filteredRows = useMemo(() => {
    return rows.filter((row) => isWithinInclusiveDateRange(row?.started_at, startDate, endDate));
  }, [endDate, rows, startDate]);

  const rows_el = useMemo(() => filteredRows.map((e, i) => {
    const onClose = () => {
      setModal();
    };
    const onClick = ({ autoplay = false }) => setModal(<TranscriptModal autoplay={autoplay} id={e.contact_id} onClose={onClose} transcript={e.transcript} />);
    let d = '';
    if (e?.duration_seconds) {
      const _d = moment.duration(e.duration_seconds, 'seconds');
      const h = Math.floor(_d.asHours());
      const m = _d.minutes();
      const s = _d.seconds();
      d = ` ${h > 0 ? String(h).padStart(2, '0') + ':' : ''}${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }
    return (
      <tr key={i}>
        <td>
          <span><b>{moment(e.started_at).format('M/D/YY h:mm:ss A')}</b></span>
          <span>{d}</span>
        </td>
        <td>
          <Link className="text-decoration-none fw-semibold text-muted" to={`/app/pipeline/subjects/${e.subject_id}?tab=contacts`}>{e.full_name_first}</Link>
        </td>
        <td>{e.channel}{auth?.groups?.includes('admin')  && e.ai_platform ? ' / ' + e.ai_platform : ''}</td>
        <td>
          <div className="my-1">
            {(e.outcomes || []).map((outcome, i) => <React.Fragment key={i}><span className={`col-auto mt-1 me-1 p-1 small badge ${outcome_color(outcome)}`}>{outcome}</span><br /></React.Fragment>)}
          </div>
        </td>
        <td className="text-start align-middle py-2">
          {e.city || dash}
        </td>
        <td className="text-start align-middle py-2">
          <div className="d-flex justify-content-between align-items-center">
            <span>{e.phone_number}</span>
            <div className="d-flex gap-2 ms-3">
  						{e.recording_key && <div className="col-auto btn-link cursor-pointer" onClick={() => onClick({ autoplay: true })}>{recordingIcon}</div>}
  						<div className="col-auto btn-link cursor-pointer" onClick={onClick}>{transcriptIcon}</div>
            </div>
          </div>
        </td>
      </tr>
    );
  }, [filteredRows]));

  return (
    <div
      className="bg-white px-4 py-3 rounded-3 shadow-sm  dashboard-card  h-100"
      style={{ border: '1px solid #e0e0e0', height: '100%'  }}
    >
      {modal}
      {/* Header */}
      <div
              className="d-flex justify-content-between align-items-center"
              style={{ marginBottom: '0.25rem' }}
            >
              <h6
                className="fw-semibold fs-5 mb-0"
                style={{
                  color: '#2E2868',
                  display: 'flex',
                position: 'relative',
                position: 'relative',
                  top: '-5px'
                  
                }}
              >
              Engagement Summary
              </h6>
      
              <div style={{
                display: 'flex',
                position: 'relative'
              }}>
                <div style={{
                  position: 'relative',
                top: '-5px'
                }}>
                  <ExpandButton onClick={handleExpand} />
                </div>
              </div>
      
            </div>

      {/* Divider */}
      <div
        style={{
          height: '2px',
          backgroundColor: '#f3f4f6',
          marginTop: '0.1rem',
          marginBottom: '0.25rem',
          width: 'calc(100% + 3rem)',
          marginLeft: '-1.5rem',
        }}
      />
                
      {/* Table */}
      <div className="dashboard-card__body" style={{ maxHeight: '320px', overflowY: 'auto' }}>
        <table style={{ minHeight: 0 }} className="table table-sm mb-0 w-100 compact-table"
        >
          <thead className="sticky-top">
            <tr className="text-muted small" >
              <th>Date & Time</th>
              <th>Subject</th>
              <th>Channel</th>
              <th>Outcomes</th>
              <th>City</th>
              <th>Phone Number</th>
            </tr>
          </thead>
          <tbody className="small">
            {rows_el}
          </tbody>
        </table>

      </div>


    </div>
  );
};

export default EngagementSummaryCard;
